/**
 * Utilities for automatic card number generation and incrementing
 */

export interface CardNumberCounter {
  lastNumber: string; // The last used card number (e.g., "042", "123")
  numericValue: number; // The numeric value (e.g., 42, 123)
  format: "simple" | "padded"; // Format type detected
  paddingLength?: number; // Length for zero-padding (e.g., 3 for "001")
  lastUpdated: Date; // Timestamp of last update
  shopId: string; // Shop identifier for isolation
}

export interface NumberFormatResult {
  format: "simple" | "padded" | "invalid";
  numericValue: number;
  paddingLength?: number;
}

/**
 * Detects the format of a card number string
 * @param cardNumber - The card number to analyze
 * @returns Format information including type, numeric value, and padding
 */
export const detectNumberFormat = (cardNumber: string): NumberFormatResult => {
  // Check if string is purely numeric
  if (!/^\d+$/.test(cardNumber)) {
    return { format: "invalid", numericValue: 0 };
  }

  const numericValue = parseInt(cardNumber, 10);

  // Check for zero-padding
  if (cardNumber.length > 1 && cardNumber.startsWith("0")) {
    return {
      format: "padded",
      numericValue,
      paddingLength: cardNumber.length,
    };
  }

  return {
    format: "simple",
    numericValue,
    paddingLength: `${numericValue}`.length,
  };
};

/**
 * Generates the next card number based on the current counter state
 * @param counter - Current counter state
 * @returns The next card number as a string
 */
export const generateNextNumber = (counter: CardNumberCounter): string => {
  const nextNumeric = counter.numericValue + 1;

  if (counter.format === "padded" && counter.paddingLength) {
    return nextNumeric.toString().padStart(counter.paddingLength, "0");
  }

  return nextNumeric.toString();
};

/**
 * Creates a new counter from an existing card number
 * @param cardNumber - The card number to base the counter on
 * @param shopId - The shop ID
 * @returns A new CardNumberCounter or null if the card number is invalid
 */
export const createCounterFromCardNumber = (
  cardNumber: string,
  shopId: string
): CardNumberCounter | null => {
  const formatResult = detectNumberFormat(cardNumber);

  if (formatResult.format === "invalid") {
    return null;
  }

  return {
    lastNumber: cardNumber,
    numericValue: formatResult.numericValue,
    format: formatResult.format,
    paddingLength: formatResult.paddingLength,
    lastUpdated: new Date(),
    shopId,
  };
};

/**
 * Updates a counter with a new card number if it's higher than the current value
 * @param counter - Current counter state
 * @param cardNumber - New card number to potentially update with
 * @returns Updated counter or original counter if no update needed
 */
export const updateCounterIfHigher = (
  counter: CardNumberCounter,
  cardNumber: string
): CardNumberCounter => {
  const formatResult = detectNumberFormat(cardNumber);

  if (formatResult.format === "invalid") {
    return counter;
  }

  // Only update if the new number is higher
  if (formatResult.numericValue > counter.numericValue) {
    return {
      ...counter,
      lastNumber: cardNumber,
      numericValue: formatResult.numericValue,
      format: formatResult.format,
      paddingLength: formatResult.paddingLength ?? 0,
      lastUpdated: new Date(),
    };
  }

  return counter;
};

/**
 * Creates a default counter for a shop with no existing cards
 * @param shopId - The shop ID
 * @returns A default counter starting at 0
 */
export const createDefaultCounter = (shopId: string): CardNumberCounter => {
  return {
    lastNumber: "0",
    numericValue: 0,
    format: "simple",
    lastUpdated: new Date(),
    shopId,
  };
};

/**
 * Validates if a card number is in a supported format for auto-incrementing
 * @param cardNumber - The card number to validate
 * @returns True if the number can be auto-incremented
 */
export const isAutoIncrementable = (cardNumber: string): boolean => {
  const formatResult = detectNumberFormat(cardNumber);
  return formatResult.format !== "invalid";
};

/**
 * Validates a shop ID for auto-increment operations
 * @param shopId - The shop ID to validate
 * @returns True if the shop ID is valid
 */
export const isValidShopId = (shopId: string): boolean => {
  return typeof shopId === "string" && shopId.trim().length > 0;
};

/**
 * Detects potential edge cases in card number formats
 * @param cardNumber - The card number to analyze
 * @returns Array of potential issues or edge cases
 */
export const detectEdgeCases = (cardNumber: string): string[] => {
  const issues: string[] = [];

  if (!cardNumber || cardNumber.trim() === "") {
    issues.push("Card number is empty");
    return issues;
  }

  const trimmed = cardNumber.trim();

  // Check for very long numbers that might cause overflow
  if (trimmed.length > 10) {
    issues.push("Card number is unusually long (over 10 digits)");
  }

  // Check for leading zeros that might be significant
  if (trimmed.length > 1 && trimmed.startsWith("0")) {
    issues.push("Card number has leading zeros - format will be preserved");
  }

  // Check for non-numeric characters
  if (!/^\d+$/.test(trimmed)) {
    issues.push(
      "Card number contains non-numeric characters - auto-increment not supported"
    );
  }

  // Check for potential overflow (JavaScript safe integer limit)
  const numericValue = parseInt(trimmed, 10);
  if (numericValue > Number.MAX_SAFE_INTEGER - 1000) {
    issues.push("Card number is close to maximum safe integer limit");
  }

  return issues;
};

/**
 * Generates a safe next number that avoids common edge cases
 * @param counter - Current counter state
 * @returns The next card number, handling edge cases
 */
export const generateSafeNextNumber = (counter: CardNumberCounter): string => {
  try {
    const nextNumeric = counter.numericValue + 1;

    // Check for overflow
    if (nextNumeric > Number.MAX_SAFE_INTEGER) {
      throw new Error("Card number would exceed maximum safe integer");
    }

    if (counter.format === "padded" && counter.paddingLength) {
      const nextPadded = nextNumeric
        .toString()
        .padStart(counter.paddingLength, "0");

      // Check if padding is still sufficient
      if (nextPadded.length > counter.paddingLength) {
        console.warn(
          `Card number ${nextNumeric} exceeds padding length ${counter.paddingLength}`
        );
        return nextNumeric.toString(); // Fall back to unpadded
      }

      return nextPadded;
    }

    return nextNumeric.toString();
  } catch (error) {
    console.error("Error generating safe next number:", error);
    return "1"; // Ultimate fallback
  }
};
